import { Button, Form, Input, Modal, Radio, Select, Switch } from "antd";
import type { RadioChangeEvent } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  createuser,
  edituser,
  retrieveUserRoles,
} from "../../redux/actions/userActions";
import { RootState } from "../../redux/rootReducer";
import { Actions, ROLE, Status } from "../../shared/constant";
import validateMessages from "../../utils/validate";
import PasswordConfirmForm from "../_shared/PasswordConfirmForm";
import UploadImage from "../_shared/UploadImage";
import globalUtils from "../../utils/globalUtils";

const UserRole = ["CLAP_ADMIN", ROLE.CLAP_MANAGER, ROLE.ACCOUNTS, ROLE.MANAGER];

interface ModalFormProps {
  visible: boolean;
  onCancel: () => void;
  mode: string;
  editData: any;
}

const UserModalForm = ({
  visible,
  onCancel,
  mode,
  editData,
}: ModalFormProps) => {
  const dispatch = useDispatch();
  const [userForm] = Form.useForm();
  const [isActive, setIsActive] = useState(true);
  const [clientDisable, setClientDisable] = useState(true);
  const [file, setFile] = useState(null);
  const { roles, pending, fulfilled } = useSelector(
    (state: RootState) => state.user,
  );
  const { allClients } = useSelector((state: RootState) => state.account);

  const sortedRoles = roles?.sort((a: any, b: any) => {
    return UserRole.indexOf(a.name) - UserRole.indexOf(b.name);
  });

  useEffect(() => {
    dispatch(retrieveUserRoles());
  }, []);

  useEffect(() => {
    if (mode === Actions.EDIT) {
      if (editData.status === Status.INACTIVE) setIsActive(false);
      userForm.setFieldsValue({
        name: editData.name,
        username: editData.username,
        email: editData.email,
        role: editData.groups[0],
        ...(editData.client && { client: editData.client }),
        status: editData.status === Status.ACTIVE,
      });
    }
  }, [editData]);

  const onToggleSwitch = (checked: boolean) => {
    setIsActive(checked);
  };

  const onRoleChange = (e: RadioChangeEvent) => {
    const role = e.target.value;
    if (role.includes(ROLE.ADMIN) || role === ROLE.CLAP_MANAGER) {
      userForm.setFieldsValue({ client: null });
      setClientDisable(true);
      return;
    }
    setClientDisable(false);
  };

  const onFileUpload = (content: any) => {
    setFile(content);
  };

  const onOk = async () => {
    try {
      await userForm.validateFields();
    } catch (errorInfo) {
      return;
    }

    // TODO update user: patch req. so send all the fields; setInitialValue=""
    const formFields = userForm.getFieldsValue();
    const formData = new FormData();
    formData.append("name", formFields.name);
    formData.append("username", formFields.username);
    formData.append("email", formFields.email);
    if (formFields.password) formData.append("password", formFields.password);
    if (file) formData.append("profile_picture", file as unknown as Blob);
    if (mode === Actions.CREATE) formData.append("groups", formFields.role);
    if (mode === Actions.CREATE && formFields.client)
      formData.append("client", formFields.client);
    formData.append(
      "status",
      formFields.status ? Status.ACTIVE : Status.INACTIVE,
    );

    // console.log("profile picture file", file);
    if (mode === Actions.CREATE) dispatch(createuser(formData));
    if (mode === Actions.EDIT) {
      dispatch(edituser({ id: editData.id, body: formData }));
    }
  };

  useEffect(() => {
    if (!visible) return;
    /* Create and Edit success */
    if (fulfilled) {
      onCancel();
    }
  }, [pending]);

  const handleCancel = () => {
    setFile(null);
    onCancel();
  };

  const afterClose = () => {
    userForm.resetFields();
    setIsActive(true);
    setFile(null);
  };

  return (
    <Modal
      visible={visible}
      title={`${mode === Actions.CREATE ? "Create" : "Edit"} User`}
      centered
      width={740}
      onCancel={handleCancel}
      afterClose={afterClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={pending as boolean}
          onClick={onOk}
        >
          {mode === Actions.CREATE ? "Create" : "Save"}
        </Button>,
      ]}
    >
      <div className="mx-6 mt-2">
        <Form
          form={userForm}
          layout="vertical"
          name="userForm"
          validateMessages={validateMessages}
        >
          <div className="mb-[26px]">
            <div className="grid grid-cols-2 gap-x-6">
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="username"
                label="User Name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </div>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true }, { type: "email" }]}
            >
              <Input />
            </Form.Item>
            <PasswordConfirmForm
              label
              formStyle="grid grid-cols-2 gap-x-6"
              required={mode === Actions.CREATE}
            />
            <Form.Item name="profile_picture" label="Profile Picture">
              <UploadImage onFileUpload={onFileUpload} />
            </Form.Item>
            <Form.Item name="role" label="Role" rules={[{ required: true }]}>
              <Radio.Group
                onChange={onRoleChange}
                disabled={mode === Actions.EDIT}
              >
                {sortedRoles?.map((role: any) => (
                  <Radio key={role.id} value={role.name}>
                    {globalUtils.snakeCaseToCapitalize(role.name)}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
            <div className="grid grid-cols-2 gap-x-6">
              <Form.Item
                name="client"
                label="Client"
                rules={[
                  { required: mode === Actions.CREATE && !clientDisable },
                ]}
              >
                <Select
                  placeholder="Select"
                  disabled={mode === Actions.CREATE ? clientDisable : true}
                  filterOption={(inputValue, option) =>
                    option?.props.children
                      .toString()
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  showSearch
                  allowClear
                >
                  {allClients?.map((client: any) => (
                    <Select.Option key={client.id} value={client.id}>
                      {client.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div className="flex gap-x-[26px] items-center">
              <div>
                <Form.Item
                  valuePropName="checked"
                  name="status"
                  className="h-4"
                  initialValue
                >
                  <Switch defaultChecked onChange={onToggleSwitch} />
                </Form.Item>
              </div>
              <div className="h-5">{isActive ? "Active" : "Inactive"}</div>
            </div>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default UserModalForm;
